/**
 * Shared image upload and type check for camera command and paste handling.
 */

const IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
] as const;

/** Image file extensions for paste/drop when MIME type is unreliable. */
const IMAGE_EXTENSIONS = [
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
  '.svg',
  '.bmp',
  '.ico',
] as const;

export function isImageFile(file: File): boolean {
  if (
    IMAGE_MIME_TYPES.includes(file.type as (typeof IMAGE_MIME_TYPES)[number])
  ) {
    return true;
  }
  const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
  return (IMAGE_EXTENSIONS as readonly string[]).includes(ext);
}

export interface UploadImageOptions {
  apiHost: string;
  userEmail: string;
  /** When set, sent as `x-upload-document-id` so the upload row links to this document. */
  documentId?: string;
  /** When true, sends `x-upload-encrypt: 1` so the server stores ciphertext. */
  encrypt?: boolean;
}

function uploadHeaders(
  options: Pick<UploadImageOptions, 'documentId' | 'encrypt'>,
): HeadersInit | undefined {
  const headers: Record<string, string> = {};
  if (options.documentId) {
    headers['x-upload-document-id'] = options.documentId;
  }
  if (options.encrypt) {
    headers['x-upload-encrypt'] = '1';
  }
  return Object.keys(headers).length > 0 ? headers : undefined;
}

export interface UploadImageResult {
  url: string;
  markdown: string;
}

/**
 * Uploads an image file to /api/upload and returns the public URL and markdown snippet.
 * Caller is responsible for toasts and insertion. Throws on validation or HTTP error.
 */
export async function uploadImageFile(
  file: File,
  options: UploadImageOptions,
): Promise<UploadImageResult> {
  if (!isImageFile(file)) {
    throw new Error('File must be an image (JPEG, PNG, GIF, or WebP)');
  }

  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${options.apiHost}/api/upload`, {
    method: 'POST',
    body: formData,
    credentials: 'include',
    headers: uploadHeaders(options),
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: 'Upload failed' }));
    throw new Error(errorData.message || `HTTP ${response.status}`);
  }

  const result = await response.json();
  const url = result.url as string;
  const altText = file.name.replace(/\.[^/.]+$/, '');
  const markdown = `![${altText}](${url})`;

  return { url, markdown };
}

export interface UploadFileResult {
  id: string;
  resourceId: string;
  url: string;
  isEncrypted: boolean;
}

/**
 * Message composer: upload immediately as a draft row until createMessage promotes it.
 */
export async function uploadFileForMessageDraft(
  file: File,
  options: Pick<UploadImageOptions, 'apiHost' | 'encrypt'>,
): Promise<UploadFileResult> {
  const formData = new FormData();
  formData.append('file', file);

  const url = new URL(`${options.apiHost}/api/upload`);
  url.searchParams.set('draft', 'true');

  const response = await fetch(url.toString(), {
    method: 'POST',
    body: formData,
    credentials: 'include',
    headers: uploadHeaders({ encrypt: options.encrypt }),
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: 'Upload failed' }));
    throw new Error(errorData.message || `HTTP ${response.status}`);
  }

  const result = await response.json();
  return {
    id: result.id as string,
    resourceId: result.resourceId as string,
    url: result.url as string,
    isEncrypted: Boolean(result.isEncrypted),
  };
}

/**
 * Uploads any file to /api/upload.
 * Used for pasting non-image files (inserted as links) and the document attach modal.
 * Throws on HTTP error.
 */
export async function uploadFile(
  file: File,
  options: UploadImageOptions,
): Promise<UploadFileResult> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${options.apiHost}/api/upload`, {
    method: 'POST',
    body: formData,
    credentials: 'include',
    headers: uploadHeaders(options),
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: 'Upload failed' }));
    throw new Error(errorData.message || `HTTP ${response.status}`);
  }

  const result = await response.json();
  return {
    id: result.id as string,
    resourceId: result.resourceId as string,
    url: result.url as string,
    isEncrypted: Boolean(result.isEncrypted),
  };
}

function markdownHrefForUpload(
  result: UploadFileResult,
  options: UploadImageOptions,
): string {
  if (result.isEncrypted) {
    return `${options.apiHost}/api/upload/${result.id}`;
  }
  return result.url;
}

/** Markdown link target for a message attachment (matches paste / document link behavior). */
export function composerAttachmentMarkdownHref(
  id: string,
  url: string,
  isEncrypted: boolean,
  apiHost: string,
): string {
  if (isEncrypted) {
    return `${apiHost}/api/upload/${id}`;
  }
  return url;
}

export function composerAttachmentMarkdownLine(
  filename: string,
  id: string,
  url: string,
  isEncrypted: boolean,
  apiHost: string,
): string {
  const href = composerAttachmentMarkdownHref(id, url, isEncrypted, apiHost);
  return `[${filename}](${href})`;
}

/**
 * Result of pasting a file: either embedded image markdown or link markdown.
 */
export interface PastedFileResult {
  markdown: string;
}

/**
 * Uploads a pasted file and returns the appropriate markdown to insert.
 * Images are embedded; other files become links.
 */
export async function uploadPastedFile(
  file: File,
  options: UploadImageOptions,
): Promise<PastedFileResult> {
  const result = await uploadFile(file, options);
  const href = markdownHrefForUpload(result, options);
  const name = file.name.replace(/\.[^/.]+$/, '') || file.name;

  if (isImageFile(file)) {
    return { markdown: `![${name}](${href})` };
  }
  return { markdown: `[${file.name}](${href})` };
}
